import type { APIError } from "core/entities";
import type {
  CentrixPathsByMethod,
  ExtractSuccessType,
} from "core/entities/APITypes";
import { useRouter } from "next/router";
import { useMemo } from "react";
import useSWR from "swr";
import { buildCentrixUrl } from "../utils/buildCentrixUrl";
import { jsonFetch } from "../utils/jsonFetch";
import type { UseCentrixApiArg } from "./useCentrixApi";
import { useFetchAllPaginatedData } from "./useFetchAllPaginatedData";

/**
 * Use to access centrix api endpoints that do not require authentication
 * Should only be used on `/public` routes or when the user is not authenticated
 */
export function useCentrixApiAnonymous<P extends CentrixPathsByMethod<"get">>({
  path,
  parameters,
  swrOptions,
  shouldFetch = true,
  fetchAll = false,
}: UseCentrixApiArg<P>) {
  const { locale } = useRouter();
  const allPaginatedDataFetcher = useFetchAllPaginatedData(jsonFetch);
  const [url, fetcher] = useMemo(() => {
    if (!fetchAll) {
      return [buildCentrixUrl({ path, parameters, locale }), jsonFetch];
    }
    const newURL = new URL(buildCentrixUrl({ path, parameters, locale }));
    newURL.searchParams.delete("MaxResultCount");
    newURL.searchParams.delete("maxResultCount");
    newURL.searchParams.delete("SkipCount");
    newURL.searchParams.delete("skipCount");
    newURL.searchParams.set("fetchAll", "true"); // this will be removed by allFetcher. Here it's being used as a flag for the SWR key
    return [newURL.toString(), allPaginatedDataFetcher];
  }, [fetchAll, path, parameters, locale, allPaginatedDataFetcher]);
  const { data, error, isLoading, mutate, isValidating } = useSWR<
    ExtractSuccessType<P>,
    APIError
  >(shouldFetch ? url : null, fetcher, swrOptions);

  return {
    data,
    isLoading,
    isError: error,
    isValidating,
    mutate,
  };
}
